import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { faqs } from '@/mocks/comics';

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于香蕉漫画 - 韩漫日漫国漫推荐大全',
    description: '香蕉漫画是一个专注于韩漫、日漫、国漫推荐的中文漫画导航平台，为漫迷提供热门漫画推荐、人气排行榜和分类浏览服务。',
    url: 'https://bananamanga.com/about',
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '香蕉漫画',
    url: 'https://bananamanga.com',
    description: '香蕉漫画精选热门韩漫、日漫、国漫分类推荐，涵盖恋爱、奇幻、热血等所有风格，为漫迷提供高清版漫画免费在线阅读和人气排行榜。',
    foundingDate: '2023',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@bananamanga.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  const stats = [
    { value: '25,000,000+', label: '月活跃用户', icon: 'ri-user-heart-line' },
    { value: '500+', label: '精选漫画', icon: 'ri-book-open-line' },
    { value: '98%', label: '用户满意度', icon: 'ri-emotion-happy-line' },
    { value: '3', label: '语言版本', icon: 'ri-global-line' },
  ];

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <ScrollToTop />
      <Navbar />

      <main>
        {/* Page Header */}
        <section className="relative h-64 md:h-80 flex items-center bg-secondary-500">
          <div className="absolute inset-0 bg-gradient-to-r from-black/25 to-transparent"></div>
          <div className="relative z-10 w-full px-4 md:px-6 max-w-[1280px] mx-auto pt-16">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mt-3">
              关于香蕉漫画
            </h1>
            <p className="text-white/75 text-sm mt-2 max-w-lg">
              为漫迷提供最好的漫画推荐服务
            </p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-10 items-center">
              <div className="flex-1">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-5">
                  我们的故事
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  香蕉漫画诞生于一群漫画爱好者的共同心愿——为中文世界的漫迷打造一个真正好用的漫画推荐平台。
                  在浩如烟海的漫画世界中，找到一部好漫画往往需要耗费大量时间。我们深知漫荒的痛苦，因此立志成为漫迷的漫画导航灯塔。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  我们的编辑团队由资深漫画读者组成，每天浏览最新漫画资讯，精心筛选值得推荐的优质作品。
                  无论你是热爱韩漫精致画风的颜值党，还是追逐日漫热血剧情的少年，抑或支持国漫崛起的爱国者，香蕉漫画都能为你找到下一部心头好。
                </p>
                <p className="text-foreground-600 leading-relaxed">
                  我们坚持免费推荐，不向读者收费。所有漫画信息均来自公开渠道整理，我们鼓励漫迷通过正版平台阅读漫画，支持原创作者。
                </p>
              </div>
              <div className="flex-shrink-0 w-full lg:w-96">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20cozy%20manga%20reading%20scene%20illustration%2C%20comfortable%20reading%20nook%20with%20books%20and%20manga%20volumes%2C%20soft%20orange%20and%20warm%20lighting%2C%20relaxing%20atmosphere%2C%20artistic%20illustration%20style%2C%20no%20text%2C%20beautiful%20composition&width=800&height=600&seq=about-manga-art&orientation=landscape"
                  alt="香蕉漫画 - 舒适阅读场景"
                  title="香蕉漫画 - 漫画推荐平台"
                  className="w-full rounded-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-5 bg-background-50 rounded-xl border border-background-200/70">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center bg-primary-100 text-primary-600 rounded-full mb-3">
                    <i className={`${stat.icon} text-xl`}></i>
                  </div>
                  <p className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">{stat.value}</p>
                  <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Reputation */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-10">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '漫迷小王',
                  text: '自从用了香蕉漫画，再也没漫荒过！分类真的很清晰，韩漫推荐太精准了，每一部都是精品。已经推荐给身边所有漫友了！',
                  avatar: '王',
                  rating: 5,
                },
                {
                  name: '二次元小仙女',
                  text: '偶然发现香蕉漫画，简直打开新世界大门。国漫推荐太棒了，发现了好多以前不知道的优秀国漫作品。排行榜更新也很及时！',
                  avatar: '仙',
                  rating: 5,
                },
                {
                  name: '深夜漫读者',
                  text: '日漫分类做得非常专业，热门新番第一时间就能在首页看到推荐。简介写得也很用心，一下子就能判断是不是自己喜欢的类型。',
                  avatar: '夜',
                  rating: 5,
                },
              ].map((review) => (
                <div key={review.name} className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-bold text-sm">
                      {review.avatar}
                    </div>
                    <div>
                      <p className="font-medium text-sm text-foreground-800">{review.name}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: review.rating }).map((_, i) => (
                          <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-3">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">关于香蕉漫画的常见疑问解答</p>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                  >
                    <h4 className="font-heading font-semibold text-sm text-foreground-800 pr-4">
                      {faq.question}
                    </h4>
                    {openFaq === index ? (
                      <i className="ri-arrow-up-s-line text-foreground-400 flex-shrink-0"></i>
                    ) : (
                      <i className="ri-arrow-down-s-line text-foreground-400 flex-shrink-0"></i>
                    )}
                  </button>
                  {openFaq === index && (
                    <div className="px-4 pb-4">
                      <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
            <div className="max-w-2xl mx-auto text-center">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500 mb-8">
                如果你有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center bg-primary-100 text-primary-600 rounded-full mb-3">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <p className="font-medium text-sm text-foreground-800">电子邮件</p>
                  <p className="text-xs text-foreground-500 mt-1">contact@bananamanga.com</p>
                </div>
                <div className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center bg-accent-100 text-accent-600 rounded-full mb-3">
                    <i className="ri-chat-smile-2-line text-lg"></i>
                  </div>
                  <p className="font-medium text-sm text-foreground-800">社交媒体</p>
                  <p className="text-xs text-foreground-500 mt-1">微信公众号：香蕉漫画</p>
                </div>
                <div className="p-5 bg-background-100 rounded-xl border border-background-200/70">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center bg-secondary-100 text-secondary-600 rounded-full mb-3">
                    <i className="ri-team-line text-lg"></i>
                  </div>
                  <p className="font-medium text-sm text-foreground-800">商务合作</p>
                  <p className="text-xs text-foreground-500 mt-1">bd@bananamanga.com</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}